import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search } from 'lucide-react';
import ProductCard from '../components/ProductCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CatalogPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('tutti');
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    axios.get(`${API}/categories`).then(({ data }) => setCategories(data.categories || [])).catch(() => {});
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (category !== 'tutti') params.category = category;
      if (search) params.search = search;
      const { data } = await axios.get(`${API}/products`, { params });
      setProducts(data.products || []);
      setTotalPages(data.pages || 1);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProducts(); }, [category, page]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchProducts();
  };

  return (
    <div data-testid="catalog-page" className="pb-safe">
      <div className="px-4 pt-4">
        <h1 className="text-2xl font-semibold text-[#2A2624] mb-4" style={{ fontFamily: 'Outfit' }}>
          Catalogo
        </h1>

        {/* Search */}
        <form onSubmit={handleSearch} className="flex items-center bg-white rounded-xl border border-[#E8E3D9] overflow-hidden mb-4">
          <Search size={18} className="ml-3 text-[#877E78]" />
          <input
            data-testid="catalog-search"
            type="text"
            placeholder="Cerca prodotti..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="flex-1 px-3 py-3 text-sm bg-transparent outline-none text-[#2A2624]"
          />
        </form>

        {/* Categories */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-3">
          <button
            key="tutti"
            data-testid="catalog-cat-tutti"
            onClick={() => { setCategory('tutti'); setPage(1); }}
            className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.08em] transition-all duration-300 ${
              category === 'tutti' ? 'bg-[#C25934] text-white' : 'bg-[#F0EBE1] text-[#5C554F] hover:bg-[#E6E2D6]'
            }`}
          >
            Tutti
          </button>
          {categories.map(cat => (
            <button
              key={cat}
              data-testid={`catalog-cat-${cat}`}
              onClick={() => { setCategory(cat); setPage(1); }}
              className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.08em] transition-all duration-300 ${
                category === cat ? 'bg-[#C25934] text-white' : 'bg-[#F0EBE1] text-[#5C554F] hover:bg-[#E6E2D6]'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Products */}
      <div className="px-4 mt-2">
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border border-[#E8E3D9] overflow-hidden">
                <div className="aspect-[4/5] skeleton-shimmer" />
                <div className="p-3 space-y-2">
                  <div className="h-4 skeleton-shimmer rounded" />
                  <div className="h-5 skeleton-shimmer rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16">
            <Search size={32} className="mx-auto text-[#877E78] mb-3" />
            <h3 className="text-lg font-medium text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
              Nessun prodotto trovato
            </h3>
            <p className="text-sm text-[#877E78] mt-1">Prova un'altra ricerca o categoria</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {products.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-6 mb-4">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                onClick={() => setPage(i + 1)}
                className={`w-9 h-9 rounded-full text-sm font-medium transition-all ${
                  page === i + 1 ? 'bg-[#C25934] text-white' : 'bg-[#F0EBE1] text-[#5C554F]'
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
